import styles from "./AppLayout.module.css";
import { Trans } from "react-i18next";
import { useContext, type JSX, type Ref, type RefObject } from "react";
import {
  AppContext,
  AppDispatchContext,
  CalculationActionTypes,
  selectPaidServices,
  selectSubsidyDependantCalculationDetails,
} from "../../state";
import { useI18n } from "../../i18n";
import { Container } from "../Container";
import { Content } from "../Content";
import { Heading } from "../Heading";
import { InfoBubbles, type BubbleType } from "../InfoBubbles";
import { Savings } from "../Savings";
import { Warnings } from "../Warnings";
import { Hint } from "../Hint";
import { Link } from "../Link";
import { CalculationDetails } from "../CalculationDetails";
import { CtaButton } from "../CTA";
import { Feedback } from "../Feedback";
import { WithCookieConsent } from "../WithCookieConsent";
import { VersionInfo } from "../VersionInfo";

type ScrollTargetRefs = {
  employerPaidCostsRef: RefObject<HTMLTableElement | null>;
  bikeSavingsRef: RefObject<HTMLTableElement | null>;
  monthlyRateRef: RefObject<HTMLDivElement | null>;
  individualSubsidyRef: RefObject<HTMLInputElement | null>;
};

type LeasingResultsProps = {
  bubbleSectionRef: Ref<HTMLElement>;
  scrollTargetRefs: ScrollTargetRefs;
  bubbleClickHandler: (bubbleType: BubbleType) => void;
  subsidyBoxClickHandler: () => void;
};

const LeasingResults = ({
  bubbleSectionRef,
  scrollTargetRefs,
  bubbleClickHandler,
  subsidyBoxClickHandler,
}: LeasingResultsProps): JSX.Element => {
  const { t } = useI18n();
  const state = useContext(AppContext);
  const dispatch = useContext(AppDispatchContext);

  const isDealerMode = state.configuration.calculatorEnv === "dealer";
  const subsidyDependantCalculationDetails = selectSubsidyDependantCalculationDetails(state);
  const { companyPays } = selectPaidServices(state);

  const handleShowResultsDetails = (showDetails: boolean): void => {
    dispatch({ type: CalculationActionTypes.SHOW_RESULTS_DETAILS, payload: showDetails });
  };

  return (
    <>
      <Container breakOutSticky increasedGap innerRef={bubbleSectionRef}>
        <Content reducedGap>
          <InfoBubbles
            monthlyRate={subsidyDependantCalculationDetails?.netChargeEmployee}
            bikeSavings={subsidyDependantCalculationDetails?.savingsBetweenPrivateAndJobrad}
            bubbleClickHandler={bubbleClickHandler}
            companyPays={companyPays}
          />
          <Warnings warnings={state.calculation.warnings} />
          <Hint variant="centered">{t("hints.allValuesRounded")}</Hint>
        </Content>
        {!isDealerMode && (
          <Content>
            <CtaButton id="JobRadRechner_CTA_FHS"></CtaButton>
          </Content>
        )}
      </Container>
      <Container>
        <Content isCentered>
          <Heading as="h3" reducedGap className={styles.savingsSectionHeading}>
            {t("savings.sectionHeading")}
          </Heading>
          <Savings scrollTargetRefs={scrollTargetRefs} subsidyBoxClickHandler={subsidyBoxClickHandler} />
          <CalculationDetails
            isExpanded={state.calculation.showResultsDetails}
            onToggle={handleShowResultsDetails}
            monthlyRateRef={scrollTargetRefs.monthlyRateRef}
          />
          <Hint variant="highlighted">{t("hints.nonBindingSampleCalculation")}</Hint>
          <Hint>
            <Trans
              i18nKey={"hints.sourceTaxCalculation"}
              t={t}
              values={{ taxationYear: state.calculation.calculator?.taxationYear }}
              components={{
                Link: <Link id="link-sourceTaxCalculation" key={"hints.sourceTaxCalculation"} target="_blank" />,
              }}
            ></Trans>
          </Hint>
          <VersionInfo />
          <WithCookieConsent>
            <Feedback />
          </WithCookieConsent>
        </Content>
      </Container>
    </>
  );
};

export { LeasingResults };
export type { ScrollTargetRefs };
